import React, { useState, useEffect } from 'react';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  property_id: string;
  property_title: string;
  property_price: number;
  request_type: 'compra' | 'renta' | 'pdf_download';
  preferred_contact_method: string;
  preferred_contact_day: string;
  preferred_contact_time: string;
  visit_requested: boolean;
  visit_date?: string;
  visit_time?: string;
  message?: string;
  status: string;
  priority: string;
  notes: string;
  assigned_to: string;
  last_contact: string;
  next_followup: string;
  created_at: string;
  interaction_type: string;
  interaction_history: any[];
  lead_score: number;
  lead_temperature: string;
  total_interactions: number;
  properties_viewed: string[];
  pdf_downloads: number;
  form_submissions: number;
  last_interaction_date: string;
}

const LeadsManagementEnhanced = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [temperatureFilter, setTemperatureFilter] = useState('all');
  const [interactionFilter, setInteractionFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [newNote, setNewNote] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        }
      });

      if (response.ok) {
        const data = await response.json();
        const mappedLeads = data.map((lead: any) => ({
          ...lead,
          status: lead.status || 'nuevo',
          priority: lead.priority || 'media',
          notes: lead.notes || '',
          assigned_to: lead.assigned_to || '',
          last_contact: lead.last_contact || '',
          next_followup: lead.next_followup || '',
          interaction_type: lead.interaction_type || 'form_request',
          interaction_history: lead.interaction_history || [],
          lead_score: lead.lead_score || 0,
          lead_temperature: lead.lead_temperature || 'cold',
          total_interactions: lead.total_interactions || 1,
          properties_viewed: lead.properties_viewed || [],
          pdf_downloads: lead.pdf_downloads || 0,
          form_submissions: lead.form_submissions || 0,
          last_interaction_date: lead.last_interaction_date || lead.created_at
        }));
        setLeads(mappedLeads);
      } else {
        console.error('Error fetching leads:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const total = leads.length;
    const enProceso = leads.filter(lead => ['contactado', 'en_proceso', 'visita_agendada', 'propuesta_enviada'].includes(lead.status)).length;
    const cerrados = leads.filter(lead => lead.status === 'cerrado').length;
    const conversion = total > 0 ? Math.round((cerrados / total) * 100) : 0;
    const hot = leads.filter(lead => lead.lead_temperature === 'hot').length;
    const warm = leads.filter(lead => lead.lead_temperature === 'warm').length;
    const cold = leads.filter(lead => lead.lead_temperature === 'cold').length;

    return { total, enProceso, cerrados, conversion, hot, warm, cold };
  };

  const stats = calculateStats();

  const updateLeadStatus = async (leadId: string, newStatus: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          status: newStatus,
          last_contact: new Date().toISOString()
        })
      });

      if (response.ok) {
        await fetchLeads();
        alert('Estado actualizado exitosamente');
      } else {
        throw new Error('Error al actualizar estado');
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
      alert('Error al actualizar estado');
    }
  };

  const updateLeadPriority = async (leadId: string, newPriority: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          priority: newPriority
        })
      });

      if (response.ok) {
        await fetchLeads();
        alert('Prioridad actualizada exitosamente');
      } else {
        throw new Error('Error al actualizar prioridad');
      }
    } catch (error) {
      console.error('Error updating lead priority:', error);
      alert('Error al actualizar prioridad');
    }
  };

  const addNote = async (leadId: string, note: string) => {
    try {
      const lead = leads.find(l => l.id === leadId);
      if (!lead) return;

      const timestamp = new Date().toLocaleString('es-MX');
      const newNotes = lead.notes ? `${lead.notes}\n\n[${timestamp}] ${note}` : `[${timestamp}] ${note}`;

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          notes: newNotes
        })
      });

      if (response.ok) {
        await fetchLeads();
        setNewNote('');
        setShowDetails(false);
        alert('Nota agregada exitosamente');
      } else {
        throw new Error('Error al agregar nota');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Error al agregar nota');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = searchTerm === '' || 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.property_title.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || lead.priority === priorityFilter;
    const matchesTemperature = temperatureFilter === 'all' || lead.lead_temperature === temperatureFilter;
    const matchesInteraction = interactionFilter === 'all' || lead.interaction_type === interactionFilter;
    
    return matchesSearch && matchesStatus && matchesPriority && matchesTemperature && matchesInteraction;
  });

  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);

  const getStatusColor = (status: string) => {
    const colors = {
      nuevo: 'bg-blue-100 text-blue-800 border-blue-300',
      contactado: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      en_proceso: 'bg-orange-100 text-orange-800 border-orange-300',
      visita_agendada: 'bg-purple-100 text-purple-800 border-purple-300',
      propuesta_enviada: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      cerrado: 'bg-green-100 text-green-800 border-green-300',
      perdido: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      baja: 'bg-gray-100 text-gray-800 border-gray-300',
      media: 'bg-blue-100 text-blue-800 border-blue-300',
      alta: 'bg-orange-100 text-orange-800 border-orange-300',
      urgente: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[priority as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getTemperatureColor = (temperature: string) => {
    const colors = {
      hot: 'bg-red-500 text-white',
      warm: 'bg-orange-500 text-white',
      cold: 'bg-blue-500 text-white'
    };
    return colors[temperature as keyof typeof colors] || 'bg-gray-500 text-white';
  };

  const getTemperatureIcon = (temperature: string) => {
    const icons = {
      hot: 'ri-fire-fill',
      warm: 'ri-sun-fill',
      cold: 'ri-snowflake-fill'
    };
    return icons[temperature as keyof typeof icons] || 'ri-temp-cold-line';
  };

  const getInteractionTypeLabel = (type: string) => {
    const labels = {
      form_request: 'Formulario',
      pdf_download: 'Ficha Técnica',
      both: 'Ambos'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getInteractionTypeColor = (type: string) => {
    const colors = {
      form_request: 'bg-green-100 text-green-800',
      pdf_download: 'bg-purple-100 text-purple-800',
      both: 'bg-blue-100 text-blue-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getInteractionTypeIcon = (type: string) => {
    const icons = {
      form_request: 'ri-file-list-3-line',
      pdf_download: 'ri-file-pdf-line',
      both: 'ri-stack-line'
    };
    return icons[type as keyof typeof icons] || 'ri-question-line';
  };

  const getUrgencyIndicator = (lead: Lead) => {
    const createdDate = new Date(lead.created_at);
    const daysSinceCreated = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (lead.status === 'nuevo' && daysSinceCreated > 2) {
      return { color: 'text-red-500', icon: 'ri-alarm-warning-fill', message: 'Lead sin atender', pulse: true };
    }
    
    if (lead.status === 'contactado' && daysSinceCreated > 5) {
      return { color: 'text-orange-500', icon: 'ri-time-fill', message: 'Requiere seguimiento', pulse: false };
    }
    
    if (lead.visit_requested && lead.status !== 'visita_agendada') {
      return { color: 'text-purple-500', icon: 'ri-calendar-line', message: 'Visita solicitada', pulse: true };
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestión Avanzada de Leads</h2>
          <p className="text-sm text-gray-600 mt-1">Sistema unificado de seguimiento con historial de interacciones</p>
        </div>
        <button
          onClick={fetchLeads}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          Actualizar
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <i className="ri-user-add-line text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">En Proceso</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.enProceso}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <i className="ri-time-line text-2xl text-yellow-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cerrados</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.cerrados}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="ri-check-double-line text-2xl text-green-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Conversión</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.conversion}%</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="ri-percent-line text-2xl text-purple-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Temperature Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <i className="ri-fire-fill text-white text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-red-900">Leads Calientes</p>
                <p className="text-2xl font-bold text-red-700">{stats.hot}</p>
              </div>
            </div>
            <div className="text-xs text-red-600 bg-red-200 px-2 py-1 rounded-full">Alta prioridad</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                <i className="ri-sun-fill text-white text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-orange-900">Leads Tibios</p>
                <p className="text-2xl font-bold text-orange-700">{stats.warm}</p>
              </div>
            </div>
            <div className="text-xs text-orange-600 bg-orange-200 px-2 py-1 rounded-full">Seguimiento</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                <i className="ri-snowflake-fill text-white text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900">Leads Fríos</p>
                <p className="text-2xl font-bold text-blue-700">{stats.cold}</p>
              </div>
            </div>
            <div className="text-xs text-blue-600 bg-blue-200 px-2 py-1 rounded-full">Nutrir</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-search-line mr-1"></i>
              Buscar
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nombre, email o propiedad..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-flag-line mr-1"></i>
              Estado
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
            >
              <option value="all">Todos</option>
              <option value="nuevo">Nuevo</option>
              <option value="contactado">Contactado</option>
              <option value="en_proceso">En Proceso</option>
              <option value="visita_agendada">Visita Agendada</option>
              <option value="propuesta_enviada">Propuesta Enviada</option>
              <option value="cerrado">Cerrado</option>
              <option value="perdido">Perdido</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-arrow-up-line mr-1"></i>
              Prioridad
            </label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
            >
              <option value="all">Todas</option>
              <option value="baja">Baja</option>
              <option value="media">Media</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-temp-hot-line mr-1"></i>
              Temperatura
            </label>
            <select
              value={temperatureFilter}
              onChange={(e) => setTemperatureFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
            >
              <option value="all">Todas</option>
              <option value="hot">🔥 Caliente</option>
              <option value="warm">☀️ Tibio</option>
              <option value="cold">❄️ Frío</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-stack-line mr-1"></i>
              Tipo
            </label>
            <select
              value={interactionFilter}
              onChange={(e) => setInteractionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
            >
              <option value="all">Todos</option>
              <option value="form_request">Formulario</option>
              <option value="pdf_download">Ficha Técnica</option>
              <option value="both">Ambos</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setPriorityFilter('all');
              setTemperatureFilter('all');
              setInteractionFilter('all');
              setCurrentPage(1);
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-2"></i>
            Limpiar Filtros
          </button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente / Temperatura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Propiedad / Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interacciones
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado / Prioridad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Puntuación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedLeads.map((lead) => {
                const urgencyIndicator = getUrgencyIndicator(lead);
                return (
                  <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          <div className={`h-12 w-12 rounded-full ${getTemperatureColor(lead.lead_temperature)} flex items-center justify-center shadow-md`}>
                            <i className={`${getTemperatureIcon(lead.lead_temperature)} text-xl`}></i>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                            {urgencyIndicator && (
                              <div className={`${urgencyIndicator.color} ${urgencyIndicator.pulse ? 'animate-pulse' : ''}`} title={urgencyIndicator.message}>
                                <i className={urgencyIndicator.icon}></i>
                              </div>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{lead.email}</div>
                          <div className="text-xs text-gray-400">{lead.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{lead.property_title}</div>
                      <div className="text-sm text-gray-500">
                        {lead.request_type === 'compra' ? '💰 Compra' : lead.request_type === 'renta' ? '🔑 Renta' : '📄 PDF'} - ${lead.property_price?.toLocaleString()}
                      </div>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getInteractionTypeColor(lead.interaction_type)}`}>
                          <i className={`${getInteractionTypeIcon(lead.interaction_type)} mr-1`}></i>
                          {getInteractionTypeLabel(lead.interaction_type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center text-xs text-gray-600">
                          <i className="ri-stack-line mr-1 text-blue-500"></i>
                          <span className="font-medium">{lead.total_interactions}</span>
                          <span className="ml-1">interacciones</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-600">
                          <i className="ri-file-pdf-line mr-1 text-purple-500"></i>
                          <span className="font-medium">{lead.pdf_downloads}</span>
                          <span className="ml-1">PDFs</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-600">
                          <i className="ri-file-list-3-line mr-1 text-green-500"></i>
                          <span className="font-medium">{lead.form_submissions}</span>
                          <span className="ml-1">formularios</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-600">
                          <i className="ri-home-line mr-1 text-orange-500"></i>
                          <span className="font-medium">{lead.properties_viewed?.length || 0}</span>
                          <span className="ml-1">propiedades</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className={`w-full px-2 py-1 text-xs font-semibold rounded-lg border ${getStatusColor(lead.status)} cursor-pointer`}
                        >
                          <option value="nuevo">🆕 Nuevo</option>
                          <option value="contactado">📞 Contactado</option>
                          <option value="en_proceso">⏳ En Proceso</option>
                          <option value="visita_agendada">📅 Visita Agendada</option>
                          <option value="propuesta_enviada">📧 Propuesta Enviada</option>
                          <option value="cerrado">✅ Cerrado</option>
                          <option value="perdido">❌ Perdido</option>
                        </select>
                        <select
                          value={lead.priority}
                          onChange={(e) => updateLeadPriority(lead.id, e.target.value)}
                          className={`w-full px-2 py-1 text-xs font-semibold rounded-lg border ${getPriorityColor(lead.priority)} cursor-pointer`}
                        >
                          <option value="baja">⬇️ Baja</option>
                          <option value="media">➖ Media</option>
                          <option value="alta">⬆️ Alta</option>
                          <option value="urgente">🚨 Urgente</option>
                        </select>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex flex-col items-center">
                        <div className={`text-2xl font-bold ${
                          lead.lead_score >= 20 ? 'text-red-600' : 
                          lead.lead_score >= 10 ? 'text-orange-600' : 
                          'text-blue-600'
                        }`}>
                          {lead.lead_score}
                        </div>
                        <div className="text-xs text-gray-500">puntos</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <a
                            href={`https://wa.me/${lead.phone.replace(/\D/g, '')}?text=Hola ${lead.name}, te contacto por tu interés en ${lead.property_title}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-center px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors cursor-pointer text-xs font-medium whitespace-nowrap"
                            title="WhatsApp"
                          >
                            <i className="ri-whatsapp-line"></i>
                          </a>
                          <a
                            href={`mailto:${lead.email}?subject=Información sobre ${lead.property_title}`}
                            className="flex-1 text-center px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors cursor-pointer text-xs font-medium whitespace-nowrap"
                            title="Email"
                          >
                            <i className="ri-mail-line"></i>
                          </a>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedLead(lead);
                            setShowDetails(true);
                          }}
                          className="w-full px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors cursor-pointer text-xs font-medium whitespace-nowrap"
                        >
                          <i className="ri-eye-line mr-1"></i>
                          Ver Historial
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                Anterior
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                Siguiente
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> a{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, filteredLeads.length)}
                  </span>{' '}
                  de <span className="font-medium">{filteredLeads.length}</span> resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let page;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium cursor-pointer ${
                          page === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full ${getTemperatureColor(selectedLead.lead_temperature)} flex items-center justify-center shadow-lg`}>
                    <i className={`${getTemperatureIcon(selectedLead.lead_temperature)} text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{selectedLead.name}</h3>
                    <p className="text-sm text-gray-600">Puntuación: {selectedLead.lead_score} puntos</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {/* Lead Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <i className="ri-stack-line text-2xl text-blue-600 mb-2"></i>
                  <div className="text-2xl font-bold text-blue-900">{selectedLead.total_interactions}</div>
                  <div className="text-xs text-blue-700">Interacciones</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <i className="ri-file-pdf-line text-2xl text-purple-600 mb-2"></i>
                  <div className="text-2xl font-bold text-purple-900">{selectedLead.pdf_downloads}</div>
                  <div className="text-xs text-purple-700">PDFs Descargados</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <i className="ri-file-list-3-line text-2xl text-green-600 mb-2"></i>
                  <div className="text-2xl font-bold text-green-900">{selectedLead.form_submissions}</div>
                  <div className="text-xs text-green-700">Formularios</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <i className="ri-home-line text-2xl text-orange-600 mb-2"></i>
                  <div className="text-2xl font-bold text-orange-900">{selectedLead.properties_viewed?.length || 0}</div>
                  <div className="text-xs text-orange-700">Propiedades Vistas</div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-user-line mr-2 text-blue-600"></i>
                    Información del Cliente
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Email:</strong> {selectedLead.email}</p>
                    <p><strong>Teléfono:</strong> {selectedLead.phone}</p>
                    {selectedLead.company && <p><strong>Empresa:</strong> {selectedLead.company}</p>}
                    <p><strong>Método preferido:</strong> {selectedLead.preferred_contact_method}</p>
                    <p><strong>Día preferido:</strong> {selectedLead.preferred_contact_day}</p>
                    <p><strong>Horario preferido:</strong> {selectedLead.preferred_contact_time}</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-home-line mr-2 text-green-600"></i>
                    Propiedad Principal
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p className="font-medium text-gray-900">{selectedLead.property_title}</p>
                    <p><strong>Tipo:</strong> {selectedLead.request_type === 'compra' ? 'Compra' : selectedLead.request_type === 'renta' ? 'Renta' : 'Descarga PDF'}</p>
                    <p><strong>Precio:</strong> ${selectedLead.property_price?.toLocaleString()}</p>
                    {selectedLead.visit_requested && (
                      <div className="mt-2 p-2 bg-purple-100 rounded">
                        <p className="text-purple-800 font-medium">
                          <i className="ri-calendar-line mr-1"></i>
                          Visita: {selectedLead.visit_date} - {selectedLead.visit_time}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Interaction History */}
              {selectedLead.interaction_history && selectedLead.interaction_history.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-history-line mr-2 text-purple-600"></i>
                    Historial de Interacciones
                  </h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {selectedLead.interaction_history.map((interaction: any, index: number) => (
                      <div key={index} className="bg-gradient-to-r from-purple-50 to-blue-50 p-3 rounded-lg border-l-4 border-purple-500">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <i className={`${interaction.type === 'pdf_download' ? 'ri-file-pdf-line text-purple-600' : 'ri-file-list-3-line text-green-600'}`}></i>
                              <span className="font-medium text-gray-900">
                                {interaction.type === 'pdf_download' ? 'Descarga de Ficha Técnica' : 'Envío de Formulario'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{interaction.property_title}</p>
                            {interaction.details && (
                              <p className="text-xs text-gray-600 mt-1">
                                {interaction.details.price && `Precio: $${interaction.details.price?.toLocaleString()}`}
                                {interaction.details.location && ` • ${interaction.details.location}`}
                              </p>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(interaction.timestamp).toLocaleString('es-MX')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Message */}
              {selectedLead.message && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-message-3-line mr-2 text-blue-600"></i>
                    Mensaje del Cliente
                  </h4>
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedLead.message}</p>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-file-text-line mr-2 text-orange-600"></i>
                  Notas de Seguimiento
                </h4>
                {selectedLead.notes ? (
                  <div className="bg-orange-50 p-4 rounded-lg max-h-40 overflow-y-auto border-l-4 border-orange-500">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{selectedLead.notes}</pre>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No hay notas de seguimiento aún.</p>
                )}
              </div>

              {/* Add Note */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-add-line mr-2 text-green-600"></i>
                  Agregar Nota de Seguimiento
                </h4>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Escribe una nota sobre la interacción con este cliente..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                />
                <div className="flex justify-end mt-4 gap-3">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="px-6 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cerrar
                  </button>
                  <button
                    onClick={() => addNote(selectedLead.id, newNote)}
                    disabled={!newNote.trim()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-save-line mr-2"></i>
                    Guardar Nota
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsManagementEnhanced;
